import React, { useEffect, useMemo, useState } from "react";
import { MemoryRouter } from "react-router-dom";
import "./novakey.css";
import products from "./data/trailProducts";
import moonfieldPolicies, { policyKeys } from "./data/moonfieldPolicies";
import Checkout from "./components/Checkout";

const cats = [
  "ALL",
  "PACKS",
  "SHELTERS",
  "SLEEP",
  "TRAIL TOOLS",
  "CAMP KITCHEN",
  "ACCESSORIES",
  "APPAREL",
  "TRAVEL",
];
export const legacyPolicyData = {
  about: [
    "We design for the distance between plans.",
    "MOONFIELD is an independent outdoor equipment studio for people who want a lighter kit without turning a trip into a spreadsheet.",
    [
      [
        "Start with the walk",
        "We begin with a real route, load and weather window. Prototypes are carried, pitched, packed wet and repaired before their shape is finalized.",
      ],
      [
        "Less, with purpose",
        "Removing weight only matters when function remains. We simplify overlapping parts, choose versatile geometry and keep high-wear areas serviceable.",
      ],
      [
        "Open field notes",
        "Product pages publish measured weight, capacity and use limits so you can compare equipment honestly.",
      ],
      [
        "Small releases",
        "We produce in deliberate runs, document revisions and keep compatible repair parts available whenever possible.",
      ],
      [
        "The long trail",
        "Our goal is equipment you understand well enough to maintain, lend and keep using across many seasons.",
      ],
    ],
  ],
  shipping: [
    "Shipping, without guesswork.",
    "Straightforward preparation, tracking and delivery guidance for every order.",
    [
      [
        "Processing",
        "In-stock orders leave in 1–3 business days. During launches or holidays, an updated estimate appears at checkout.",
      ],
      [
        "Delivery windows",
        "Standard delivery usually arrives 4–8 business days after dispatch; remote regions and customs review may extend transit.",
      ],
      [
        "Tracking",
        "A tracking link is emailed after the carrier's first scan. Mixed-stock orders may ship separately at no added charge.",
      ],
      [
        "Duties",
        "Collected taxes appear at checkout. Where duties are not prepaid, the destination authority may charge the recipient.",
      ],
      [
        "Lost or damaged parcels",
        "Contact us after seven business days without movement. Keep damaged packaging and send clear photos within seven days of delivery.",
      ],
      [
        "Packaging",
        "We use right-sized recycled cartons and paper-based protection where weatherproofing allows.",
      ],
    ],
  ],
  returns: [
    "Take it outside. Decide with context.",
    "Unused standard products can be returned within 30 days; selected equipment includes a trail-check option described below.",
    [
      [
        "Standard returns",
        "Items must be clean, complete and free of outdoor use, odor, pet hair and modification.",
      ],
      [
        "Trail check",
        "Packs and shelters marked Trail Check may be evaluated indoors or on one dry local outing within 14 days.",
      ],
      [
        "Start a return",
        "Email support@moonfield.example with the order number, item and reason before sending anything.",
      ],
      [
        "Return shipping",
        "Preference returns use tracked shipping at the customer's cost. Confirmed defects receive a prepaid method.",
      ],
      [
        "Refund timing",
        "We inspect within five business days. Your provider may need another 3–10 business days to post credit.",
      ],
      [
        "Final sale and hygiene",
        "Clearance samples, cut cord, opened water-contact items and worn apparel are final unless faulty.",
      ],
      [
        "Local rights",
        "Mandatory consumer rights in your place of residence always prevail.",
      ],
    ],
  ],
  warranty: [
    "Repair first. Replace when needed.",
    "MOONFIELD equipment is covered against defects in materials and workmanship for its practical product life.",
    [
      [
        "What is covered",
        "Manufacturing faults such as failed stitching, hardware defects and fabric flaws present under normal intended use.",
      ],
      [
        "What is wear",
        "Abrasion, puncture, UV aging, accidents, animal damage and use outside the stated load or weather range are not defects.",
      ],
      [
        "Assessment",
        "Send order details, photos and a short account of what happened. We may request the clean product for inspection.",
      ],
      [
        "Resolution",
        "Depending on condition and parts, we may repair, provide a component, replace the product or offer a fair credit.",
      ],
      [
        "Field repairs",
        "A sensible temporary repair does not automatically void coverage; tell us what was used so we can advise the permanent fix.",
      ],
      [
        "Transfer and law",
        "Coverage applies to the original purchaser and sits alongside rights that cannot legally be excluded.",
      ],
    ],
  ],
  privacy: [
    "Your route is yours.",
    "We collect only the information needed to run the store, support your order and improve site reliability.",
    [
      [
        "What we collect",
        "Contact, delivery, order and support details, plus limited device and site-use information.",
      ],
      [
        "Why we use it",
        "To take payment, deliver orders, prevent fraud, answer questions and meet legal obligations.",
      ],
      [
        "Service partners",
        "Necessary information may go to payment, hosting, fulfillment, carrier and support providers under contract.",
      ],
      [
        "Retention",
        "Order records are kept for tax, warranty and dispute periods; marketing preferences can be changed at any time.",
      ],
      [
        "Security",
        "We limit access and encrypt data in transit, while recognizing no online system can promise absolute security.",
      ],
      [
        "Your choices",
        "Depending on location, you may request access, correction, deletion or restriction at privacy@moonfield.example.",
      ],
    ],
  ],
  care: [
    "Keep good gear moving.",
    "Simple cleaning, drying and small repairs preserve performance better than frequent replacement.",
    [
      [
        "After a trip",
        "Empty every pocket, shake out grit and let equipment dry completely before storage.",
      ],
      [
        "Washing",
        "Use cool water, a soft brush and mild technical cleaner. Avoid softener, bleach, dry cleaning and high heat.",
      ],
      [
        "Waterproof gear",
        "Rinse salt and dust, then restore water repellency only when water stops beading after cleaning.",
      ],
      [
        "Zippers and poles",
        "Brush zipper teeth, keep pole joints clean and dry, and never force a section that contains grit.",
      ],
      [
        "Storage",
        "Keep shelters and quilts loose in a dry, shaded space. Release strap tension and remove batteries.",
      ],
      [
        "Repairs",
        "Patch small tears promptly. Contact us for compatible fabric, buckles, pole sections and repair guidance.",
      ],
    ],
  ],
};

export default function App() {
  const readRoute = () => {
    const raw = window.location.hash.replace(/^#\/?/, "");
    const [route = "home", arg] = raw.split("/").map(decodeURIComponent);
    return {
      route: ["home", "shop", "detail", "cart", "checkout", "policy"].includes(route)
        ? route
        : "home",
      arg,
    };
  };
  const initial = readRoute(),
    initialProduct =
      products.find((p) => String(p.id) === initial.arg) || products[0];
  const [page, setPage] = useState(initial.route),
    [filter, setFilter] = useState(
      initial.route === "shop" && cats.includes(initial.arg)
        ? initial.arg
        : "ALL",
    ),
    [selected, setSelected] = useState(initialProduct),
    [cart, setCart] = useState(() => {
      try {
        return JSON.parse(localStorage.getItem("moonfield-cart") || "[]");
      } catch {
        return [];
      }
    }),
    [menu, setMenu] = useState(false),
    [search, setSearch] = useState(""),
    [policy, setPolicy] = useState(
      initial.route === "policy" && policyKeys.includes(initial.arg)
        ? initial.arg
        : "about",
    ),
    [choice, setChoice] = useState(initialProduct.options[0]),
    [photo, setPhoto] = useState(0);
  useEffect(
    () => localStorage.setItem("moonfield-cart", JSON.stringify(cart)),
    [cart],
  );
  useEffect(() => {
    const sync = () => {
      const { route, arg } = readRoute();
      setPage(route);
      if (route === "policy" && policyKeys.includes(arg)) setPolicy(arg);
      if (route === "shop" && cats.includes(arg)) setFilter(arg);
      if (route === "detail") {
        const item = products.find((p) => String(p.id) === arg);
        if (item) {
          setSelected(item);
          setChoice(item.options[0]);
          setPhoto(0);
        }
      }
      setMenu(false);
      window.scrollTo(0, 0);
    };
    window.addEventListener("hashchange", sync);
    if (!window.location.hash) window.history.replaceState(null, "", "#/home");
    return () => window.removeEventListener("hashchange", sync);
  }, []);
  const list = useMemo(
    () =>
      products.filter(
        (p) =>
          (filter === "ALL" || p.category === filter) &&
          p.title.toLowerCase().includes(search.toLowerCase()),
      ),
    [filter, search],
  );
  const go = (p, arg) => {
      const next = `#/${p}${arg ? `/${encodeURIComponent(arg)}` : ""}`;
      if (window.location.hash === next) {
        setPage(p);
        window.scrollTo(0, 0);
      } else window.location.hash = next;
    },
    shop = (c) => {
      setFilter(c);
      go("shop", c);
    },
    open = (p) => {
      setSelected(p);
      setChoice(p.options[0]);
      setPhoto(0);
      go("detail", p.id);
    },
    show = (k) => {
      setPolicy(k);
      go("policy", k);
    };
  const add = (p) =>
    setCart((c) => {
      const x = c.find((i) => i.id === p.id && i.choice === choice);
      return x
        ? c.map((i) => (i === x ? { ...i, qty: i.qty + 1 } : i))
        : [...c, { ...p, choice, qty: 1 }];
    });
  const count = cart.reduce((n, x) => n + x.qty, 0),
    total = cart.reduce((n, x) => n + x.price * x.qty, 0);
  const changeQty = (item, delta) =>
    setCart((current) =>
      current
        .map((entry) =>
          entry.id === item.id && entry.choice === item.choice
            ? { ...entry, qty: entry.qty + delta }
            : entry,
        )
        .filter((entry) => entry.qty > 0),
    );
  if (page === "checkout") {
    const checkoutItems = cart.map((item) => ({
      ...item,
      quantity: item.qty,
      variant: item.choice,
      images: item.images?.length ? item.images : [item.image],
    }));
    return (
      <MemoryRouter initialEntries={["/checkout"]}>
        <Checkout items={checkoutItems} onBack={() => go("cart")} />
      </MemoryRouter>
    );
  }
  return (
    <div className="tk">
      <header className="top">
        <button className="menu" onClick={() => setMenu(!menu)}>
          <i />
          <i /> MENU
        </button>
        <button className="brand" onClick={() => go("home")}>
          moon<span>field</span>
          <small>FIELD EQUIPMENT</small>
        </button>
        <div>
          <button onClick={() => shop("ALL")}>SEARCH</button>
          <button onClick={() => go("cart")}>
            KIT BAG <b>{count}</b>
          </button>
        </div>
      </header>
      <nav className={`mega ${menu ? "open" : ""}`}>
        <div className="mega-head">
          <b>CHOOSE A SYSTEM</b>
          <button onClick={() => setMenu(false)}>CLOSE ×</button>
        </div>
        {cats.slice(1).map((c, i) => (
          <button onClick={() => shop(c)} key={c}>
            <span>0{i + 1}</span>
            {c}
            <b>→</b>
          </button>
        ))}
        <p>
          Questions about fit or load?
          <button onClick={() => show("care")}>Open the field guide →</button>
        </p>
      </nav>
      {page === "home" && (
        <main>
          <section className="hero">
            <img
              src="/images/trailkind/hero.png"
              alt="Hikers crossing a desert ridge at dawn"
            />
            <div className="hero-copy">
              <span>FIELD RELEASE 01 / HIGH DESERT</span>
              <h1>
                Carry the
                <br />
                day, <em>not the weight.</em>
              </h1>
              <p>
                A 28-piece trail system for moving farther with exactly what
                matters.
              </p>
              <button onClick={() => shop("ALL")}>
                BUILD YOUR KIT <b>↗</b>
              </button>
            </div>
            <div className="hero-meter">
              <span>34° 16' N</span>
              <i />
              <span>6:42 AM</span>
              <i />
              <span>12.4 MI</span>
            </div>
          </section>
          <section className="route">
            <aside>
              <span>START HERE / 01</span>
              <h2>Pick your line.</h2>
              <p>
                Shop by the part of the journey you are solving—not by a wall of
                products.
              </p>
            </aside>
            <div>
              {[
                ["01", "CARRY", "PACKS", "PACKS"],
                ["02", "COVER", "SHELTERS", "SHELTERS"],
                ["03", "RECOVER", "SLEEP", "SLEEP"],
                ["04", "MOVE", "TRAIL TOOLS", "TRAIL TOOLS"],
              ].map((x) => (
                <button onClick={() => shop(x[3])} key={x[0]}>
                  <small>{x[0]}</small>
                  <h3>{x[1]}</h3>
                  <p>{x[2]}</p>
                  <b>↗</b>
                </button>
              ))}
            </div>
          </section>
          <section className="edit">
            <header>
              <span>THE 1.8 KG BASE / 02</span>
              <h2>
                Four objects.
                <br />
                <em>One honest shelter.</em>
              </h2>
              <p>A starting point for a solo weekend, not a rigid checklist.</p>
            </header>
            <div className="edit-grid">
              {products.slice(0, 4).map((p, i) => (
                <ProductCard p={p} open={open} n={i + 1} key={p.id} />
              ))}
            </div>
          </section>
          <section className="field-note">
            <div>
              <span>FIELD NOTE / CANYON 07</span>
              <h2>
                Designed around
                <br />
                the pause.
              </h2>
              <p>
                Fast miles are useful. So are the minutes when you stop, take
                the pack off and look back at the ground you covered.
              </p>
              <button onClick={() => show("about")}>WHY WE MAKE GEAR →</button>
            </div>
            <aside>
              <b>01</b>
              <p>Weights are measured, not estimated.</p>
              <b>02</b>
              <p>High-wear parts stay replaceable.</p>
              <b>03</b>
              <p>Every product earns two jobs.</p>
            </aside>
          </section>
          <section className="signal">
            <span>TRAIL SIGNAL / WEEK 41</span>
            <h2>
              What are you carrying
              <br />
              <em>that you never use?</em>
            </h2>
            <p>
              One practical field letter each month. Route notes, repair skills,
              no noise.
            </p>
            <form onSubmit={(e) => e.preventDefault()}>
              <input type="email" placeholder="EMAIL ADDRESS" />
              <button>JOIN THE LETTER ↗</button>
            </form>
          </section>
        </main>
      )}
      {page === "shop" && (
        <main className="shop">
          <header>
            <span>GEAR INDEX / {String(list.length).padStart(2, "0")}</span>
            <h1>
              Build a kit
              <br />
              <em>that moves.</em>
            </h1>
            <p>
              Original field equipment from 28 g to 1,090 g. Compare the
              numbers; choose for the route.
            </p>
          </header>
          <div className="tools">
            <div>
              {cats.map((c) => (
                <button
                  className={filter === c ? "active" : ""}
                  onClick={() => setFilter(c)}
                  key={c}
                >
                  {c}
                </button>
              ))}
            </div>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="SEARCH GEAR"
            />
          </div>
          <div className="catalog">
            {list.map((p, i) => (
              <ProductCard p={p} open={open} n={i + 1} key={p.id} />
            ))}
          </div>
        </main>
      )}
      {page === "detail" && (
        <main className="detail">
          <section className="product-shot">
            <button onClick={() => go("shop")}>← GEAR INDEX</button>
            <div className="main-photo">
              <img
                src={selected.images[photo]}
                alt={`${selected.title} — view ${photo + 1}`}
              />
              <span>
                VIEW {photo + 1} / {selected.images.length}
              </span>
            </div>
            <div className="photo-rail">
              {selected.images.map((src, i) => (
                <button
                  className={photo === i ? "active" : ""}
                  onClick={() => setPhoto(i)}
                  key={src}
                >
                  <img src={src} alt={`${selected.title} thumbnail ${i + 1}`} />
                  <b>0{i + 1}</b>
                </button>
              ))}
            </div>
            <span>MF / {String(selected.id).padStart(3, "0")}</span>
          </section>
          <aside>
            <span>
              {selected.category} / {selected.color}
            </span>
            <h1>{selected.title}</h1>
            <p className="summary">{selected.summary}</p>
            <p className="product-lead">{selected.lead}</p>
            <div className="spec-line">
              <div>
                <small>WEIGHT</small>
                <b>{selected.weight}</b>
              </div>
              <div>
                <small>SIZE / VOLUME</small>
                <b>{selected.spec}</b>
              </div>
              <div>
                <small>PRICE</small>
                <b>${selected.price}</b>
              </div>
            </div>
            {selected.options.length > 1 && (
              <>
                <label>SELECT SIZE</label>
                <div className="options">
                  {selected.options.map((o) => (
                    <button
                      className={choice === o ? "active" : ""}
                      onClick={() => setChoice(o)}
                      key={o}
                    >
                      {o}
                    </button>
                  ))}
                </div>
              </>
            )}
            <button
              className="add"
              onClick={() => {
                add(selected);
                go("cart");
              }}
            >
              ADD TO KIT <b>${selected.price}</b>
            </button>
            <p className="shipping">Free shipping over $150 · 30-day returns</p>
            {selected.details.map(([h, b], i) => (
              <details open={i === 0} key={h}>
                <summary>
                  {h}
                  <b>+</b>
                </summary>
                <p>{b}</p>
              </details>
            ))}
          </aside>
        </main>
      )}
      {page === "cart" && <CartView cart={cart} count={count} total={total} shop={shop} open={open} go={go} setCart={setCart} changeQty={changeQty} />}
      {page === "home" && <HomeExtra open={open} shop={shop} />}{" "}
      {page === "policy" && (
        <Policy data={moonfieldPolicies[policy]} active={policy} show={show} />
      )}
      <Footer shop={shop} show={show} go={go} />
    </div>
  );
}

function CartView({cart,count,total,shop,open,go,setCart,changeQty}){
 const freeShipping=Math.max(0,150-total),progress=Math.min(100,(total/150)*100);
 if(!cart.length)return <main className="cart cart-redesign"><div className="cart-shell"><header className="cart-title"><span>KIT BAG / 00</span><h1>Your kit is<br/><em>ready for a route.</em></h1></header><section className="cart-empty"><span>00 ITEMS</span><h2>Nothing packed yet.</h2><p>Start with one essential, then build around the conditions ahead.</p><button onClick={()=>shop("ALL")}>EXPLORE ALL GEAR →</button></section></div></main>;
 return <main className="cart cart-redesign"><div className="cart-shell"><header className="cart-title"><div><span>KIT BAG / {String(count).padStart(2,"0")}</span><h1>Your trail kit.</h1></div><button onClick={()=>shop("ALL")}>CONTINUE SHOPPING →</button></header><div className="cart-layout"><section className="cart-list"><div className="cart-columns"><span>PRODUCT</span><span>QUANTITY</span><span>TOTAL</span></div>{cart.map((p)=><article key={`${p.id}-${p.choice}`}><button className="cart-product-image" onClick={()=>open(p)}><img src={p.image} alt={p.title}/></button><div className="cart-product-copy"><small>{p.category} / {p.weight}</small><button onClick={()=>open(p)}><h2>{p.title}</h2></button><span>{p.choice}</span><button className="cart-remove" onClick={()=>setCart(c=>c.filter(x=>x!==p))}>REMOVE</button></div><div className="cart-quantity" aria-label={`Quantity for ${p.title}`}><button onClick={()=>changeQty(p,-1)} aria-label="Decrease quantity">−</button><span>{String(p.qty).padStart(2,"0")}</span><button onClick={()=>changeQty(p,1)} aria-label="Increase quantity">+</button></div><strong>${(p.price*p.qty).toFixed(2)}</strong></article>)}</section><aside className="cart-summary"><span>ORDER SUMMARY</span><h2>${total.toFixed(2)}</h2><div className="shipping-progress"><p>{freeShipping>0?<>Add <b>${freeShipping.toFixed(2)}</b> for complimentary shipping.</>:<>Your order qualifies for <b>complimentary shipping.</b></>}</p><i><b style={{width:`${progress}%`}}/></i></div><dl><div><dt>Subtotal</dt><dd>${total.toFixed(2)}</dd></div><div><dt>Shipping</dt><dd>{total>=150?"COMPLIMENTARY":"CALCULATED NEXT"}</dd></div><div><dt>Estimated total</dt><dd>${total.toFixed(2)}</dd></div></dl><button className="cart-checkout" onClick={()=>go("checkout")}>SECURE CHECKOUT <b>→</b></button><small>Taxes and final delivery options are shown at checkout.</small><div className="cart-assurance"><p><b>30-DAY RETURNS</b><span>Try your setup with confidence.</span></p><p><b>FIELD SUPPORT</b><span>Real help from people who use the gear.</span></p></div></aside></div></div></main>
}

function HomeExtra({ open, shop }) {
  const featured = products.slice(4, 12),
    categories = [
      ["PACKS", products[0]],
      ["SHELTERS", products[5]],
      ["SLEEP", products[9]],
    ];
  return (
    <>
      <section className="home-featured">
        <header>
          <div>
            <span>FIELD FAVORITES / 03</span>
            <h2>
              More trail.
              <br />
              Less guesswork.
            </h2>
          </div>
          <button onClick={() => shop("ALL")}>SHOP ALL 28 PIECES →</button>
        </header>
        <div className="home-product-grid">
          {featured.map((p, i) => (
            <ProductCard p={p} open={open} n={i + 5} key={p.id} />
          ))}
        </div>
      </section>
      <section className="home-category-images">
        {categories.map(([category, p], i) => (
          <button onClick={() => shop(category)} key={category}>
            <img src={p.image} alt={`${category.toLowerCase()} collection`} />
            <span>0{i + 1} / SHOP</span>
            <h3>{category}</h3>
            <b>EXPLORE →</b>
          </button>
        ))}
      </section>
      <section className="home-pick">
        <div className="home-pick-image">
          <img src={products[12].image} alt={products[12].title} />
          <span>EDITOR'S PICK / {products[12].weight}</span>
        </div>
        <div>
          <small>ONE PIECE / TWO JOBS</small>
          <h2>{products[12].title}</h2>
          <p>{products[12].summary}</p>
          <div>
            <b>{products[12].spec}</b>
            <strong>${products[12].price}</strong>
          </div>
          <button onClick={() => open(products[12])}>VIEW PRODUCT →</button>
        </div>
      </section>
    </>
  );
}
function ProductCard({ p, open, n }) {
  return (
    <article className="product-card" onClick={() => open(p)}>
      <div>
        <img src={p.image} alt={p.title} />
        <span>{p.weight}</span>
        <button>VIEW GEAR ↗</button>
      </div>
      <small>
        {String(n).padStart(2, "0")} / {p.category}
      </small>
      <h3>{p.title}</h3>
      <p>
        {p.spec}
        <b>${p.price}</b>
      </p>
    </article>
  );
}
function Policy({ data, active, show }) {
  const wordCount = [data.intro, ...data.sections.map(([, body]) => body)]
    .join(" ")
    .trim()
    .split(/\s+/).length;
  const activeIndex = policyKeys.indexOf(active),
    readTime = Math.ceil(wordCount / 220);
  const jump = (id) =>
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  return (
    <main className="policy policy-journal">
      <section className="policy-utility">
        <div>
          <span>MOONFIELD</span>
          <i />
          FIELD DESK
        </div>
        <div>
          <b>POLICY LIBRARY</b>
          <span>26 DOCUMENTS</span>
          <span>2026 EDITION</span>
        </div>
      </section>
      <header className="policy-cover">
        <div className="policy-breadcrumb">
          <span>POLICY {String(activeIndex + 1).padStart(2, "0")} / 26</span>
          <span>{readTime} MIN READ</span>
          <span>{wordCount.toLocaleString()} WORDS</span>
        </div>
        <h1>{data.title}</h1>
        <p>{data.intro}</p>
        <div className="policy-rule">
          <b>PLAIN LANGUAGE STANDARD</b>
          <i />
          <b>REVIEWED OCTOBER 2026</b>
        </div>
      </header>
      <div className="policy-workspace">
        <aside className="policy-library">
          <div className="policy-library-head">
            <span>POLICY DIRECTORY</span>
            <b>{String(activeIndex + 1).padStart(2, "0")} / 26</b>
          </div>
          <nav aria-label="All policies">
            {policyKeys.map((k, i) => (
              <button
                className={active === k ? "active" : ""}
                onClick={() => show(k)}
                key={k}
              >
                <small>{String(i + 1).padStart(2, "0")}</small>
                <span>{moonfieldPolicies[k].title}</span>
                <b>→</b>
              </button>
            ))}
          </nav>
        </aside>
        <article className="policy-reader">
          <div className="policy-reader-intro">
            <span>CONTENTS</span>
            <h2>
              Everything you need
              <br />
              to know.
            </h2>
            <p>
              Choose a section below or read from the beginning. If your
              situation is not covered, our field desk will review the complete
              context.
            </p>
          </div>
          <nav className="policy-toc" aria-label="On this page">
            {data.sections.map(([h], i) => (
              <button onClick={() => jump(`policy-section-${i + 1}`)} key={h}>
                <small>{String(i + 1).padStart(2, "0")}</small>
                <span>{h}</span>
              </button>
            ))}
          </nav>
          <div className="policy-sections">
            {data.sections.map(([h, b], i) => (
              <section id={`policy-section-${i + 1}`} key={h}>
                <div className="policy-section-meta">
                  <span>{String(i + 1).padStart(2, "0")}</span>
                  <i />
                </div>
                <div>
                  <h2>{h}</h2>
                  <p>{b}</p>
                  <small>MOONFIELD / {active.toUpperCase()}</small>
                </div>
              </section>
            ))}
          </div>
        </article>
      </div>
      <section className="policy-contact">
        <div>
          <span>NEED HELP?</span>
          <h2>Ask the field desk.</h2>
        </div>
        <div>
          <p>
            Include your order number, product name and the outcome you need. We
            usually reply within two working days.
          </p>
          <a href="mailto:support@moonfield.example">
            SUPPORT@MOONFIELD.EXAMPLE <b>↗</b>
          </a>
          <small>MONDAY–FRIDAY · 09:00–17:00 CST</small>
        </div>
      </section>
    </main>
  );
}
function Footer({ shop, show, go }) {
  return (
    <footer className="site-footer">
      <div className="footer-brand">
        <button onClick={() => go("home")}>
          moon<span>field</span>
        </button>
        <p>Ultralight equipment for a wider world.</p>
        <span>PACK LESS / NOTICE MORE</span>
      </div>
      <div className="footer-gear">
        <b>GEAR</b>
        {[
          "PACKS",
          "SHELTERS",
          "SLEEP",
          "TRAIL TOOLS",
          "CAMP KITCHEN",
          "ACCESSORIES",
          "APPAREL",
          "TRAVEL",
        ].map((x) => (
          <button onClick={() => shop(x)} key={x}>
            {x}
          </button>
        ))}
      </div>
      <div className="footer-policies">
        <b>POLICY DIRECTORY / 26</b>
        <div>
          {policyKeys.map((x, i) => (
            <button onClick={() => show(x)} key={x}>
              <small>{String(i + 1).padStart(2, "0")}</small>
              {moonfieldPolicies[x].title}
            </button>
          ))}
        </div>
      </div>
      <aside>
        <span>
          FIELD EQUIPMENT
          <br />
          FOR A WIDER WORLD
        </span>
        <b>© 2026 MOONFIELD EQUIPMENT</b>
      </aside>
    </footer>
  );
}
